import React, { useState, useEffect } from 'react'
import { Button } from '../components/ui/Button'
import { CustomDateTimeFilter, CustomDateTimeRange } from '../components/filters/CustomDateTimeFilter'

interface LogFile {
  name: string
  display_name: string
  size: number
  modified: string
}

interface LogData {
  lines: string[]
  total_lines_in_file: number
  filtered_lines: number
  file_name: string
  time_filter: string
  cutoff_time: string
  pagination?: {
    current_page: number
    page_size: number
    total_pages: number
    total_filtered: number
    has_next: boolean
    has_prev: boolean
    showing_start: number
    showing_end: number
  }
}

const LogsPage: React.FC = () => {
  const [logFiles, setLogFiles] = useState<LogFile[]>([])
  const [selectedLogFile, setSelectedLogFile] = useState<string>('')
  const [timeFilter, setTimeFilter] = useState<string>('1h')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [logLevel, setLogLevel] = useState<string>('')
  const [logData, setLogData] = useState<LogData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFiles, setIsLoadingFiles] = useState(true)
  const [customDateTimeRange, setCustomDateTimeRange] = useState<CustomDateTimeRange | null>(null)
  const [customDateTimeEnabled, setCustomDateTimeEnabled] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(1000)

  // Time filter options
  const timeFilterOptions = [
    { value: '5m', label: 'Last 5 minutes' },
    { value: '15m', label: 'Last 15 minutes' },
    { value: '30m', label: 'Last 30 minutes' },
    { value: '1h', label: 'Last 1 hour' },
    { value: '4h', label: 'Last 4 hours' },
    { value: '12h', label: 'Last 12 hours' },
    { value: '24h', label: 'Last 24 hours' }
  ]

  // Log level options
  const logLevelOptions = [
    { value: '', label: 'All Levels' },
    { value: 'DEBUG', label: 'Debug' },
    { value: 'INFO', label: 'Info' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'ERROR', label: 'Error' },
    { value: 'CRITICAL', label: 'Critical' }
  ]

  // Load available log files
  useEffect(() => {
    const loadLogFiles = async () => {
      try {
        const response = await fetch('/api/v1/logs/files', {
          credentials: 'include'
        })
        const result = await response.json()
        
        if (result.success) {
          setLogFiles(result.data)
          // Auto-select the first log file
          if (result.data.length > 0) {
            setSelectedLogFile(result.data[0].name)
          }
        }
      } catch (error) {
        console.error('Failed to load log files:', error)
      } finally {
        setIsLoadingFiles(false)
      }
    }

    loadLogFiles()
  }, [])

  // Handle custom date/time filter changes
  const handleCustomDateTimeChange = (range: CustomDateTimeRange | null) => {
    setCustomDateTimeRange(range)
    if (range) {
      setCustomDateTimeEnabled(true)
    }
  }

  const handleCustomDateTimeModeToggle = (enabled: boolean) => {
    setCustomDateTimeEnabled(enabled)
    if (!enabled) {
      setCustomDateTimeRange(null)
    }
  }

  // Load log content when filters change
  const loadLogContent = async () => {
    if (!selectedLogFile) return

    setIsLoading(true)
    try {
      const requestBody = {
        log_file: selectedLogFile,
        time_filter: timeFilter,
        search_term: searchTerm || undefined,
        log_level: logLevel || undefined,
        custom_date_time_range: customDateTimeRange || undefined,
        page: currentPage,
        page_size: pageSize
      }
      
      console.log('Loading logs with request:', requestBody)
      
      const response = await fetch('/api/v1/logs/read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      })

      const result = await response.json()
      
      console.log('Log API response:', result)
      
      if (result.success) {
        console.log('Pagination data:', result.data.pagination)
        setLogData(result.data)
      } else {
        console.error('Failed to load log content:', result)
      }
    } catch (error) {
      console.error('Failed to load log content:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Auto-load when file or filters change
  useEffect(() => {
    if (selectedLogFile) {
      loadLogContent()
    }
  }, [selectedLogFile, timeFilter, customDateTimeRange, currentPage, pageSize])

  // Reset page when filters change (but not page/pageSize)
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedLogFile, timeFilter, customDateTimeRange])

  // Reload when search term or log level changes
  useEffect(() => {
    if (selectedLogFile) {
      const timer = setTimeout(() => {
        loadLogContent()
      }, 500) // Debounce search
      return () => clearTimeout(timer)
    }
  }, [searchTerm, logLevel])

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Format log line with syntax highlighting
  const formatLogLine = (line: string, index: number) => {
    let className = 'font-mono text-sm py-1 px-2 border-b border-gray-100'
    
    // Color code by log level
    if (line.includes(' - ERROR - ')) {
      className += ' bg-red-50 text-red-800'
    } else if (line.includes(' - WARNING - ')) {
      className += ' bg-yellow-50 text-yellow-800'
    } else if (line.includes(' - INFO - ')) {
      className += ' bg-blue-50 text-blue-800'
    } else if (line.includes(' - DEBUG - ')) {
      className += ' bg-gray-50 text-gray-600'
    } else {
      className += ' text-gray-700'
    }

    return (
      <div key={index} className={className}>
        {line}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">System Logs</h1>
        <p className="text-gray-600">
          View and filter system logs, application events, and debugging information for monitoring and troubleshooting.
        </p>
      </div>

      {/* Log Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Log File Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Log File
            </label>
            <select
              value={selectedLogFile}
              onChange={(e) => setSelectedLogFile(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingFiles}
            >
              {isLoadingFiles ? (
                <option>Loading...</option>
              ) : (
                logFiles.map((file) => (
                  <option key={file.name} value={file.name}>
                    {file.display_name} ({formatFileSize(file.size)})
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Time Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Range
            </label>
            {customDateTimeEnabled && (
              <div className="mb-2 bg-amber-50 border border-amber-200 rounded-md p-2">
                <p className="text-xs text-amber-800">Custom date/time filter is active</p>
              </div>
            )}
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              disabled={customDateTimeEnabled}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                customDateTimeEnabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            >
              {timeFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Log Level Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Log Level
            </label>
            <select
              value={logLevel}
              onChange={(e) => setLogLevel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {logLevelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search Term */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="flex">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search logs..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button
                onClick={loadLogContent}
                disabled={isLoading || !selectedLogFile}
                className="rounded-l-none"
              >
                {isLoading ? 'Loading...' : 'Search'}
              </Button>
            </div>
          </div>
        </div>

        {/* Custom Date/Time Filter */}
        <div className="mb-6">
          <CustomDateTimeFilter
            value={customDateTimeRange}
            onChange={handleCustomDateTimeChange}
            onToggleCustomMode={handleCustomDateTimeModeToggle}
            isCustomModeEnabled={customDateTimeEnabled}
          />
        </div>

        {/* Log Stats and Controls */}
        {logData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">File:</span>
                  <span className="ml-2 text-gray-600">{logData.file_name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Total Lines:</span>
                  <span className="ml-2 text-gray-600">{logData.total_lines_in_file.toLocaleString()}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Filtered:</span>
                  <span className="ml-2 text-gray-600">{logData.filtered_lines.toLocaleString()}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Since:</span>
                  <span className="ml-2 text-gray-600">
                    {logData.cutoff_time ? new Date(logData.cutoff_time).toLocaleString() : 'All time'}
                  </span>
                </div>
              </div>
              
              <Button
                onClick={() => {
                  console.log('Refreshing logs - Current page:', currentPage, 'Page size:', pageSize)
                  loadLogContent()
                }}
                disabled={isLoading}
                variant="secondary"
                className="ml-4"
              >
                {isLoading ? 'Refreshing...' : 'Refresh Logs'}
              </Button>
            </div>
            
          </div>
        )}
      </div>

      {/* Log Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Log Content</h2>
          {logData && logData.pagination && (
            <p className="text-sm text-gray-600">
              Showing lines {logData.pagination.showing_start}-{logData.pagination.showing_end} of {logData.pagination.total_filtered} filtered lines 
              ({logData.total_lines_in_file} total in {logData.file_name})
            </p>
          )}
        </div>
        
        {/* Pagination Controls (Always Visible for Testing) */}
        <div className="p-4 border-b border-gray-200 bg-blue-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-700">
              {logData?.pagination ? (
                <>Showing {logData.pagination.showing_start}-{logData.pagination.showing_end} of {logData.pagination.total_filtered} filtered lines (Page {logData.pagination.current_page} of {logData.pagination.total_pages})</>
              ) : (
                <>Page {currentPage}, Size: {pageSize} lines {logData ? `(${logData.lines?.length || 0} lines loaded)` : ''}</>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => {
                  console.log('First page clicked')
                  setCurrentPage(1)
                }}
                disabled={currentPage <= 1}
                variant="secondary"
                size="sm"
              >
                First
              </Button>
              <Button
                onClick={() => {
                  console.log('Previous page clicked, going from', currentPage, 'to', currentPage - 1)
                  setCurrentPage(currentPage - 1)
                }}
                disabled={currentPage <= 1}
                variant="secondary"
                size="sm"
              >
                Previous
              </Button>
              
              <span className="px-2 py-1 text-sm bg-white border rounded">
                Page {currentPage}
              </span>
              
              <Button
                onClick={() => {
                  console.log('Next page clicked, going from', currentPage, 'to', currentPage + 1)
                  setCurrentPage(currentPage + 1)
                }}
                disabled={logData?.pagination ? !logData.pagination.has_next : false}
                variant="secondary"
                size="sm"
              >
                Next
              </Button>
              <Button
                onClick={() => {
                  const lastPage = logData?.pagination?.total_pages || 1
                  console.log('Last page clicked, going to page', lastPage)
                  setCurrentPage(lastPage)
                }}
                disabled={logData?.pagination ? !logData.pagination.has_next : false}
                variant="secondary"
                size="sm"
              >
                Last
              </Button>
              
              <select
                value={pageSize}
                onChange={(e) => {
                  console.log('Page size changed to:', e.target.value)
                  setPageSize(parseInt(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="100">100 lines</option>
                <option value="500">500 lines</option>
                <option value="1000">1000 lines</option>
                <option value="2000">2000 lines</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading logs...</p>
            </div>
          ) : logData?.lines.length ? (
            <div className="divide-y divide-gray-100">
              {logData.lines.map((line, index) => formatLogLine(line, index))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              {selectedLogFile ? 'No log entries found for the selected filters.' : 'Select a log file to view content.'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LogsPage
